<template>
    <div>
        <v-container
            v-if="data.type !== 'plot'"
            fluid
            class="alternative-menu"
        >
            <v-row
                no-gutters
                align="center"
                justify="center"
            >
                <v-col
                    v-if="backButton"
                    cols="1"
                    class="leftSection"
                >
                    <div class="btn-menu btn-back">
                        <a
                            href="#"
                            @click.prevent="goBack()"
                        >
                            <v-icon class="icon">
                                icon-return
                            </v-icon>
                        </a>
                    </div>
                    <div
                        v-if="data.activeHotspot"
                        ref="hotspotInfoBtn"
                        class="btn-menu btn-hotspot"
                        :class="{ active: hotspotInfoOpen }"
                    >
                        <a
                            href="#"
                            @click.prevent="switchHotspotInfo();"
                            @blur="closePopup()"
                        >
                            <i class="icon icon-hotspot"/>
                        </a>
                    </div>
                </v-col>
                <v-col
                    class="midSection"
                    :cols="!backButton ? (!visiblePopup ? 12 : 11) : (!visiblePopup ? 11 : 9)"
                >
                    <GeneralLayout
                        v-if="data.type === 'general' && data.levels.length > 1"
                        :data="data"
                        :backButton ="backButton"
                    />
                    <CutawayLayout
                        v-else-if="data.type === 'cutaway' || data.type === 'plot-grid'"
                        :data="data"
                        :widthSidebar="widthSidebar"
                        :isOpenedSidebar="isOpenedSidebar"
                        :isOpenedProgramInfo="isOpenedProgramInfo"
                    />
                    <ModelsLayout
                        v-else-if="data.type === 'models'"
                        :data="data"
                    />
                    <DocumentationLayout
                        v-else-if="data.type === 'documentation'"
                        :data="data"
                        :backButton ="backButton"
                    />
                </v-col>
                <v-col
                    v-if="visiblePopup"
                    class="rightSection"
                    cols="2"
                >
                    <div
                        class="alternative-menu-popup"
                        ref="popup"
                        :class="{
                            'alternative-menu-popup__left': isLeft,
                            'alternative-menu-popup__right': isRight
                        }"
                        :style="{
                            position: 'absolute',
                            left: popupPosition.left + 'px',
                            top: popupPosition.top + 'px'
                        }"
                    >
                        <img
                            class="leftSide"
                            src="@/assets/images/sidebar-color-pin.png"
                            alt="image description"
                        >
                        <div class="rightSide">
                            <div class="title">
                                {{ data.activeHotspot.text }}
                            </div>
                            <div
                                class="description"
                                v-html="data.activeHotspot.description"
                            />
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-container>

        <div
            v-else
            class="alternative-menu"
        >
            <PlotLayout
                v-if="data.type === 'plot'"
                :data="data"
                :backButton="backButton"
                :goBack="goBack"
            />
        </div>

        <!-- Program Information -->
        <div v-if="hasProgramInfo || logos">
            <InformationCard
                v-if="data.program"
                :programName="data.program.name"
                :isOpenedProgramInfo="isOpenedProgramInfo"
                :isOpenedSidebar="isOpenedSidebar"
            />

            <transition name="slideright">
                <v-navigation-drawer
                    v-if="isOpenedProgramInfo"
                    class="programInfoPopup"
                    v-click-outside="closeProgramInfo"
                    :width="sidebarWidth"
                    :touchless="true"
                    app
                    permanent
                    scroll-target="#info-prog-content"
                >
                    <a
                        @click.prevent="closeProgramInfo();"
                        class="popup-close_custom pr-3 pt-2"
                    >
                        <span/>
                    </a>

                    <v-list
                        v-if="logos"
                        class="pa-0"
                    >
                        <v-list-item class="popup-header">
                            <v-list-item-content class="pa-6 text-center">
                                <v-img
                                    :src="programLogo"
                                    width="100%"
                                    max-height="100px"
                                    contain
                                />
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>

                    <v-list class="mx-6 my-8">
                        <v-list-item>
                            <v-sheet
                                id="info-prog-content"
                                class="overflow-y-auto"
                                :maxHeight="sidebarHeight"
                                :width="widthSidebar"
                            >
                                <v-row
                                    v-if="buttonTopVisible"
                                    id="scroll-top"
                                    dense
                                    no-gutters
                                    class="justify-center scroll-cta"
                                >
                                    <v-btn
                                        icon
                                        elevation="0"
                                        @click.prevent="scrollToTop"
                                    >
                                        <v-icon class="icon">
                                            icon-tri-up
                                        </v-icon>
                                    </v-btn>
                                </v-row>

                                <v-list>
                                    <v-list-item v-scroll:#info-prog-content="onScroll">
                                        <ProgramInformation :handler="handler"/>
                                    </v-list-item>
                                </v-list>

                                <v-row
                                    v-if="buttonBottomVisible"
                                    id="scroll-bottom"
                                    dense
                                    no-gutters
                                    class="justify-center scroll-cta"
                                >
                                    <v-btn
                                        icon
                                        elevation="0"
                                        @click.prevent="scrollToBottom"
                                    >
                                        <v-icon class="icon">
                                            icon-tri-down
                                        </v-icon>
                                    </v-btn>
                                </v-row>
                            </v-sheet>
                        </v-list-item>
                    </v-list>
                </v-navigation-drawer>
            </transition>
        </div>
    </div>
</template>

<script>
    import { EventBus } from '@/event-bus';
    import { debounce } from "lodash";
    import { TweenLite } from "gsap";
    import { mapGetters } from 'vuex';

    import GeneralLayout from '@/components/navbarLayout/alternativeMenu/GeneralLayout';
    import CutawayLayout from '@/components/navbarLayout/alternativeMenu/CutawayLayout';
    import ModelsLayout from '@/components/navbarLayout/alternativeMenu/ModelsLayout';
    import PlotLayout from '@/components/navbarLayout/alternativeMenu/PlotLayout';
    import DocumentationLayout from '@/components/navbarLayout/alternativeMenu/DocumentationLayout';
    import ProgramInformation from "@/components/programInformation/ProgramInformation";
    import InformationCard from "@/components/programInformation/InformationCard";

    export default {
        name: 'AlternativeMenu',
        components: {
            InformationCard,
            ProgramInformation,
            GeneralLayout,
            CutawayLayout,
            ModelsLayout,
            PlotLayout,
            DocumentationLayout
        },
        props: [
            'data',
            'backButton',
            'goBack',
            'isStandalone',
            'isMobile',
            'widthSidebar',
            'isOpenedSidebar',
            'isOpenedProgramInfo'
        ],
        data() {
            return {
                hotspotInfoOpen: false,
                visiblePopup: false,
                popupPosition: null,
                isLeft: false,
                isRight: false,
                widgetReady: false,
                scrollValue: 0,
                scrollMax: 0,
                sidebarHeight: null,
                sidebarWidth: 340
            };
        },
        computed: {
            ...mapGetters([
                'logos',
                'programInfo'
            ]),
            promoterLogo() {
                return this.logos && 'promoter' in this.logos ? this.logos['promoter'] : null;
            },
            programLogo() {
                return this.logos && 'program' in this.logos ? this.logos['program'] : null;
            },
            buttonTopVisible() {
                return this.scrollValue > 0;
            },
            buttonBottomVisible() {
                return this.scrollValue !== this.scrollMax;
            },
            hasProgramInfo() {
                return this.programInfo ? true : false;
            }
        },
        mounted() {
            this.handler();
            document.addEventListener('visibilitychange', this.handler);
            window.addEventListener('DOMContentLoaded', this.handler);
            window.addEventListener('resize', debounce(this.handler, 400));
        },
        methods: {
            handler() {
                if (this.programInfoVisible && this.isOpenedProgramInfo) {
                    let sidebarHeaderHeight = this.$refs['info-prog-header'] ? this.$refs['info-prog-header'].$el.clientHeight : 0;
                    this.sidebarHeight = window.innerHeight - sidebarHeaderHeight;
                    let sidebarDOMElement = document.getElementById('info-prog-content');
                    this.scrollValue = sidebarDOMElement.scrollTop;
                    this.scrollMax = sidebarDOMElement.scrollHeight - sidebarDOMElement.clientHeight;
                }
            },
            scrollToTop() {
                TweenLite.to(
                    document.getElementById('info-prog-content'),
                    .5,
                    {
                        scrollTop: 0
                    }
                );
            },
            scrollToBottom() {
                let sidebarDOMElement = document.getElementById('info-prog-content');
                let maxScrollTop = sidebarDOMElement.scrollHeight - sidebarDOMElement.clientHeight;
                TweenLite.to(
                    document.getElementById('info-prog-content'),
                    .5,
                    {
                        scrollTop: maxScrollTop
                    }
                );
            },
            onScroll(e) {
                this.scrollValue = e.target.scrollTop;
            },
            closePopup() {
                this.hotspotInfoOpen = false;
                this.visiblePopup = false;
            },
            switchHotspotInfo() {
                let hotspotInfoOpen = !hotspotInfoOpen;
                if (this.visiblePopup) {
                    this.visiblePopup = false;
                } else {
                    let rect = this.$refs.hotspotInfoBtn.getBoundingClientRect();
                    let mobile = window.matchMedia("(max-width: 380px)");
                    if (mobile.matches) {
                        this.popupPosition = {
                            left: rect.left - 54,
                            top: rect.top + rect.height + 12
                        }
                    }else {
                        this.popupPosition = {
                            left: rect.left,
                            top: rect.top + rect.height + 12
                        }
                    }
                    this.visiblePopup = true;
                }
            },
            closeProgramInfo() {
                EventBus.$emit('closeProgramInfo');
            }
        },
        watch: {
            visiblePopup(val) {
                this.$nextTick(() => {
                    if (val) {
                        this.isLeft = (this.popupPosition.left - this.$refs.popup.clientWidth / 2) < 0;
                        this.isRight = (this.popupPosition.left + this.$refs.popup.clientWidth / 2) > window.clientWidth;
                    }
                })
            },
            isOpenedProgramInfo(val) {
                if (val) {
                    this.$nextTick(() => {
                        this.handler();
                        document.addEventListener('visibilitychange', this.handler);
                        window.addEventListener('DOMContentLoaded', this.handler);
                        window.addEventListener('resize', debounce(this.handler, 400));
                    });
                }
            },
        }
    }
</script>

<style lang="scss">
    @import "@/assets/scss/components/_alternative-menu.scss";
    @import "@/assets/scss/components/_dropdown-menu.scss";
    @import "@/assets/scss/components/_information-card.scss";
    @import "@/assets/scss/base/vuetify/components/_alternative-menu.scss";
</style>



// WEBPACK FOOTER //
// src/components/AlternativeMenu.vue